import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { quantileLabel } from '@/lib/quantiles';
import { IntervalCard } from '@/pages/aneel/IntervalCard';
import { SharePanel } from '@/pages/aneel/SharePanel';
import {
  aneelTipeParams,
  fetchAneelBidding,
  fetchAneelTipeRows,
  fetchAneelTiRows,
} from '@/services/aneel';
import type {
  AneelFilterOption,
  AneelSanctionMonths,
  AneelTipeRow,
  AneelTipeWaterfall,
  AneelTiRow,
} from '@/services/types';

const PAGE_SIZE = 50;

/** A full CNPJ, the only key `/aneel/e/:cnpj` accepts. */
const CNPJ_DIGITS = 14;

/**
 * ANEEL's two non-infraction tracks, on one screen and fenced from each other.
 *
 * They share a page because both are small and both sit outside the Auto de
 * Infração corpus — never because they are the same instrument. Two refusals
 * define the TI half: board review is absent from all 404 rows (outcome AND
 * date), and the table carries no amount column at all, so no money figure is
 * computable at any coverage. The second is a different kind of refusal from the
 * first and says so: no future load of the same table can answer it.
 *
 * The TIPE half is an event log. `penalty_type` describes the ROW, not the case
 * — 63 rows typed as a warning carry a post-manifestation fine, because they are
 * the later stage of a case whose original penalty was written on a different
 * row. So money is never summed by type, and the waterfall runs only over the
 * 143 rows carrying both ends rather than the 261 carrying an original.
 */
export function AneelBiddingPage() {
  const { t, i18n } = useTranslation(['aneel', 'common']);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['aneel', 'bidding'],
    queryFn: fetchAneelBidding,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={8} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('aneel:bidding.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('aneel:bidding.subtitle')}</p>
      </header>

      {/* Before either track. Every figure on this page is dangerous read as an
          enforcement number: different instrument, different penalty vocabulary,
          different population. */}
      <p
        role="alert"
        className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-foreground"
      >
        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>{t('aneel:bidding.notEnforcement')}</span>
      </p>

      <ExplainerCard i18nKey="aneel:bidding.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/aneel/export/bidding" />
      </div>

      <AsOfStrip asOf={data.as_of} />

      {/* ---------------------------------------------------------------- */}
      {/* TI */}
      {/* ---------------------------------------------------------------- */}

      <section className="space-y-3 rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold text-foreground">{t('aneel:bidding.ti.title')}</h2>
          {/* Each track states its OWN span. They differ by two years and the
              shared strip above carries only the union. */}
          <span className="text-xs tabular-nums text-muted-foreground">{data.ti_span}</span>
        </div>

        <p className="text-xs text-muted-foreground">{t('aneel:bidding.ti.fence')}</p>

        <Answer answer={data.ti} help={t('aneel:bidding.ti.help')}>
          {(ti) => (
            <div className="space-y-3">
              <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {(
                  [
                    ['cases', ti.cases],
                    ['withType', ti.with_penalty_type],
                    ['withManifestation', ti.with_manifestation],
                    ['closed', ti.closed],
                  ] as const
                ).map(([key, value]) => (
                  <div key={key} className="rounded-md border bg-background p-3">
                    <dt className="text-xs text-muted-foreground">
                      {t(`aneel:bidding.ti.${key}`)}
                    </dt>
                    <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground">
                      {number.format(value)}
                    </dd>
                  </div>
                ))}
              </dl>

              <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                {t('aneel:bidding.ti.population', {
                  entities: number.format(ti.entities),
                  overlap: number.format(ti.entities_with_enforcement),
                  only: number.format(ti.entities - ti.entities_with_enforcement),
                })}
              </p>

              <div className="grid gap-3 lg:grid-cols-2">
                {/* An intimation can carry several penalty types, so these
                    shares sum above 100%. The note is derived from the same
                    numbers the panel prints rather than hard-coded, so it
                    disappears by itself if the source becomes single-label. */}
                <SharePanel
                  title={t('aneel:bidding.ti.types')}
                  shares={ti.penalty_types}
                  total={ti.with_penalty_type}
                  multiLabelNote={(labels, cases) =>
                    t('aneel:bidding.ti.multiLabel', {
                      cases: number.format(cases),
                      labels: number.format(labels),
                    })
                  }
                />
                <SharePanel
                  title={t('aneel:bidding.ti.outcomes')}
                  shares={ti.manifestation_outcomes}
                  total={ti.with_manifestation}
                />
              </div>
            </div>
          )}
        </Answer>

        {/* Two refusals of DIFFERENT kinds, side by side. One is a gap in what
            was loaded; the other is structural and no reload can fix it. */}
        <div className="grid gap-3 lg:grid-cols-2">
          <div className="rounded-md border bg-background p-3">
            <h3 className="mb-2 text-xs font-medium text-foreground">
              {t('aneel:bidding.ti.boardTitle')}
            </h3>
            {/* Not `() => null`: the refusal is what renders today, but if the
                ETL ever populates the column this panel would otherwise show a
                heading above nothing, which reads as a broken panel rather
                than as data. */}
            <Answer answer={data.ti_board} help={t('aneel:bidding.ti.boardHelp')}>
              {(board) => (
                <p className="text-sm text-foreground">
                  {t('aneel:bidding.ti.boardCoverage', {
                    count: board.with_board_outcome,
                    cases: board.cases,
                  })}
                </p>
              )}
            </Answer>
          </div>
          <div className="rounded-md border bg-background p-3">
            <h3 className="mb-2 text-xs font-medium text-foreground">
              {t('aneel:bidding.ti.moneyTitle')}
            </h3>
            <Answer answer={data.ti_money} help={t('aneel:bidding.ti.moneyHelp')}>
              {() => null}
            </Answer>
          </div>
        </div>

        {/* How long each step took, over the intimations that completed it:
            the enforcement timeline's ladder under this track's own scope. */}
        <div className="space-y-3" data-ti-intervals>
          <h3 className="text-xs font-medium text-foreground">
            {t('aneel:bidding.ti.intervals.title')}
          </h3>
          {data.ti_intervals.map((step) => (
            <IntervalCard key={step.stage} step={step} labelKey="aneel:bidding.ti.intervals" />
          ))}
        </div>

        <FieldGuide i18nKey="aneel:bidding.guide.ti" />

        <TiTable />
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* TIPE */}
      {/* ---------------------------------------------------------------- */}

      <section className="space-y-3 rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold text-foreground">
            {t('aneel:bidding.tipe.title')}
          </h2>
          <span className="text-xs tabular-nums text-muted-foreground">{data.tipe_span}</span>
        </div>

        {/* Each section carries the fence in its own right. The page header has
            one, but this section ends in a R$1.37bn figure and someone who
            scrolls to it has passed the header — and seven enforcement screens
            in the same nav report amounts in the same units. */}
        <p className="text-xs text-muted-foreground">{t('aneel:bidding.tipe.fence')}</p>

        <Answer answer={data.tipe} help={t('aneel:bidding.tipe.help')}>
          {(tipe) => (
            <div className="space-y-3">
              <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {(
                  [
                    ['cases', tipe.cases],
                    ['rows', tipe.detail_rows],
                    ['multiRow', tipe.cases_with_several_rows],
                    ['entities', tipe.entities],
                  ] as const
                ).map(([key, value]) => (
                  <div key={key} className="rounded-md border bg-background p-3">
                    <dt className="text-xs text-muted-foreground">
                      {t(`aneel:bidding.tipe.${key}`)}
                    </dt>
                    <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground">
                      {number.format(value)}
                    </dd>
                  </div>
                ))}
              </dl>

              {/* Closes two orphans. The cards say 179 cases while the grain
                  warning below says "127 of 177", and nothing explained the
                  gap; and the overlap with the enforcement corpus was measured
                  but never shown, so this track looked like it might be the
                  same companies under a different instrument. */}
              <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                {t('aneel:bidding.tipe.population', {
                  cases: number.format(tipe.cases),
                  withDetail: number.format(tipe.cases_with_detail),
                  entities: number.format(tipe.entities),
                  overlap: number.format(tipe.entities_with_enforcement),
                })}
              </p>

              {/* The grain warning sits ABOVE the type table, because the type
                  table is exactly what a reader would otherwise sum money by. */}
              <p
                role="note"
                className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-foreground"
              >
                {t('aneel:bidding.tipe.rowGrain', {
                  multi: number.format(tipe.cases_with_several_rows),
                  cases: number.format(tipe.cases_with_detail),
                })}
              </p>

              <SharePanel
                title={t('aneel:bidding.tipe.types')}
                shares={tipe.penalty_types}
                total={tipe.detail_rows}
              />
            </div>
          )}
        </Answer>

        <Answer answer={data.tipe_waterfall} help={t('aneel:bidding.tipe.waterfallHelp')}>
          {(w) => (
            <div className="space-y-3 rounded-md border bg-background p-3">
              <h3 className="text-xs font-medium text-foreground">
                {t('aneel:bidding.tipe.waterfallTitle')}
              </h3>
              <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Figure label={t('aneel:bidding.tipe.original')} value={currency.format(w.original_total)} />
                <Figure label={t('aneel:bidding.tipe.after')} value={currency.format(w.after_total)} />
                <Figure label={t('aneel:bidding.tipe.relief')} value={currency.format(w.relief)} />
                <Figure
                  label={t('aneel:bidding.tipe.movement')}
                  value={`${number.format(w.reduced)} / ${number.format(w.unchanged)} / ${number.format(w.increased)}`}
                />
              </dl>
              {/* The denominator it is NOT using, stated. 143 of 261 rows carry
                  both ends; comparing against 261 would imply the other 118
                  fines were left untouched. */}
              <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                {t('aneel:bidding.tipe.denominator', {
                  rows: number.format(w.rows),
                  withOriginal: number.format(w.rows_with_original),
                  increased: number.format(w.increased),
                  share: percent.format(w.rows / w.rows_with_original),
                })}
              </p>
            </div>
          )}
        </Answer>

        {/* The board stage, over ITS rows: post-manifestation against post-board.
            A separate population from the manifestation stage above, never
            chained into one three-bar waterfall the source cannot support. */}
        <Answer answer={data.tipe_board_waterfall} help={t('aneel:bidding.tipe.board.help')}>
          {(w) => <BoardWaterfall waterfall={w} />}
        </Answer>

        <Answer answer={data.sanction_months} help={t('aneel:bidding.tipe.months.help')}>
          {(months) => <MonthsPanel months={months} />}
        </Answer>

        <FieldGuide i18nKey="aneel:bidding.guide.tipe" />

        <TipeTable />
      </section>
    </div>
  );
}

function BoardWaterfall({ waterfall: w }: { waterfall: AneelTipeWaterfall }) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <div className="space-y-3 rounded-md border bg-background p-3" data-board-waterfall>
      <h3 className="text-xs font-medium text-foreground">{t('aneel:bidding.tipe.board.title')}</h3>
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Figure label={t('aneel:bidding.tipe.board.before')} value={currency.format(w.original_total)} />
        <Figure label={t('aneel:bidding.tipe.board.after')} value={currency.format(w.after_total)} />
        <Figure label={t('aneel:bidding.tipe.relief')} value={currency.format(w.relief)} />
        <Figure
          label={t('aneel:bidding.tipe.movement')}
          value={`${number.format(w.reduced)} / ${number.format(w.unchanged)} / ${number.format(w.increased)}`}
        />
      </dl>
      <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
        {t('aneel:bidding.tipe.denominator', {
          rows: number.format(w.rows),
          withOriginal: number.format(w.rows_with_original),
          increased: number.format(w.increased),
          share: percent.format(w.rows_with_original ? w.rows / w.rows_with_original : 0),
        })}
      </p>
    </div>
  );
}

function MonthsPanel({ months }: { months: AneelSanctionMonths }) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 0 });

  return (
    <div className="space-y-3 rounded-md border bg-background p-3" data-sanction-months>
      <h3 className="text-xs font-medium text-foreground">{t('aneel:bidding.tipe.months.title')}</h3>
      <div className="grid gap-3 lg:grid-cols-2">
        {months.types.map((row) => (
          <div key={row.penalty_type} className="rounded-md border p-3" data-months-type={row.penalty_type}>
            <p className="mb-2 text-xs font-medium text-foreground">
              {row.penalty_type}{' '}
              <span className="font-normal text-muted-foreground">
                ({t('aneel:bidding.tipe.months.rows', { n: number.format(row.n) })})
              </span>
            </p>
            {row.summary ? (
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs sm:grid-cols-5">
                {Object.entries(row.summary.quantiles).map(([key, value]) => (
                  <div key={key}>
                    <dt className="text-muted-foreground">{quantileLabel(t, key)}</dt>
                    <dd className="font-medium tabular-nums text-foreground">
                      {number.format(value)} {t('aneel:bidding.tipe.months.months')}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t('aneel:bidding.tipe.months.floor', { n: number.format(row.n) })}
              </p>
            )}
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        {t('aneel:bidding.tipe.months.board', { n: number.format(months.board_rows) })}
      </p>
      {/* A sanction row is a stage, never a status: said beside the ladder a
          reader would otherwise take for a list of barred companies. */}
      <p
        role="note"
        className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-foreground"
      >
        {t('aneel:bidding.tipe.months.stage')}
      </p>
    </div>
  );
}

/** A company cell: linked when the row carries the full CNPJ the profile route keys on. */
function CompanyCell({ name, cnpj }: { name: string | null; cnpj: string | null }) {
  if (!name) return <>—</>;
  if (cnpj && cnpj.length === CNPJ_DIGITS) {
    return (
      <Link
        to={`/aneel/e/${cnpj}`}
        className="text-primary underline-offset-4 hover:underline"
        title={name}
      >
        {name}
      </Link>
    );
  }
  return <span title={name}>{name}</span>;
}

/** Batches appended as the reader scrolls, with the load-more button kept as the real control. */
function LoadMore({
  hasNextPage,
  isFetchingNextPage,
  loadMore,
  total,
}: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  loadMore: () => void;
  total: number;
}) {
  const { t, i18n } = useTranslation(['common']);
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <div className="flex justify-center py-3">
      {hasNextPage ? (
        <button
          type="button"
          onClick={loadMore}
          disabled={isFetchingNextPage}
          className="rounded-md border bg-background px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
        >
          {isFetchingNextPage
            ? t('common:pagination.loadingMore')
            : t('common:pagination.loadMore', { count: PAGE_SIZE })}
        </button>
      ) : (
        <p className="text-xs text-muted-foreground">
          {t('common:pagination.allLoaded', { total: number.format(total) })}
        </p>
      )}
    </div>
  );
}

function OptionSelect({
  id,
  label,
  any,
  value,
  onChange,
  options,
}: {
  id: string;
  label: string;
  any: string;
  value: string;
  onChange: (value: string) => void;
  options?: AneelFilterOption[];
}) {
  const { i18n } = useTranslation();
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs font-medium text-foreground">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="max-w-[14rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
      >
        <option value="">{any}</option>
        {(options ?? []).map((option) => (
          <option key={option.value} value={option.value}>
            {option.value} ({number.format(option.cases)})
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * The intimations, filtered and batched. Its own query: the corpus panels
 * above read one payload, the rows read another, so a filter here never
 * re-fetches the whole screen.
 */
function TiTable() {
  const { t, i18n } = useTranslation(['aneel', 'common']);
  const number = new Intl.NumberFormat(i18n.language);
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState<string | null>(null);
  const [nature, setNature] = useState('');
  const [penaltyType, setPenaltyType] = useState('');
  const filters = { nature: nature || null, penalty_type: penaltyType || null, query };

  const { data: paged, isPending, error, refetch, isPlaceholderData, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['aneel', 'bidding', 'ti', filters],
      queryFn: ({ pageParam }) => fetchAneelTiRows({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
        return loaded < lastPage.total ? loaded : undefined;
      },
      placeholderData: keepPreviousData,
    });
  const data = paged?.pages[0];
  const items: AneelTiRow[] = paged?.pages.flatMap((page) => page.items) ?? [];
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({ hasNextPage, isFetchingNextPage, fetchNextPage });
  const options = data?.options ?? {};

  return (
    <div className="space-y-3" data-ti-table>
      <form
        className="flex flex-wrap items-end gap-3 rounded-md border bg-background p-3"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(draft.trim() || null);
        }}
      >
        <div className="min-w-[14rem] flex-1 space-y-1">
          <label htmlFor="aneel-ti-query" className="text-xs font-medium text-foreground">
            {t('aneel:bidding.ti.filters.search')}
          </label>
          <input
            id="aneel-ti-query"
            type="search"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('aneel:bidding.ti.filters.placeholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </div>
        <OptionSelect
          id="aneel-ti-nature"
          label={t('aneel:bidding.ti.filters.nature')}
          any={t('aneel:bidding.ti.filters.any')}
          value={nature}
          onChange={setNature}
          options={options.natures}
        />
        <OptionSelect
          id="aneel-ti-type"
          label={t('aneel:bidding.ti.filters.type')}
          any={t('aneel:bidding.ti.filters.any')}
          value={penaltyType}
          onChange={setPenaltyType}
          options={options.penalty_types}
        />
        <button
          type="submit"
          className="rounded-md border bg-foreground px-3 py-1.5 text-sm font-medium text-background"
        >
          {t('aneel:bidding.ti.filters.submit')}
        </button>
      </form>

      {error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : isPending || !data ? (
        <SkeletonLoader rows={4} />
      ) : (
        <>
          <p
            className="text-xs text-muted-foreground"
            aria-live="polite"
            data-testid="aneel-ti-total"
            data-loaded={items.length}
            data-total={data.total}
            data-stale={String(isPlaceholderData)}
          >
            {t('common:pagination.loaded', {
              loaded: number.format(items.length),
              total: number.format(data.total),
            })}
          </p>
          {items.length === 0 ? (
            <EmptyState hint={t('aneel:bidding.ti.emptyHint')} />
          ) : (
            <div
              ref={scrollRef}
              className="max-h-[60vh] overflow-auto rounded-md border"
              role="region"
              aria-label={t('aneel:bidding.ti.tableLabel')}
            >
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('aneel:bidding.ti.columns.number')}</th>
                    <th className="px-3 py-2 font-medium">{t('aneel:bidding.ti.columns.date')}</th>
                    <th className="px-3 py-2 font-medium">{t('aneel:bidding.ti.columns.company')}</th>
                    <th className="px-3 py-2 font-medium">{t('aneel:bidding.ti.columns.type')}</th>
                    <th className="px-3 py-2 font-medium">{t('aneel:bidding.ti.columns.outcome')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.intimation_number} className="border-t align-top">
                      {/* No link: there is no TI detail route. A page for one of
                          these would show nothing the row does not already carry. */}
                      <td className="px-3 py-2 tabular-nums text-foreground">{row.intimation_number}</td>
                      <td className="px-3 py-2 tabular-nums text-muted-foreground">
                        {row.intimation_date ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-foreground">
                        <CompanyCell name={row.entity_name} cnpj={row.entity_cnpj} />
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {row.penalty_types.join('; ') || '—'}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {row.manifestation_outcome ?? t('aneel:bidding.ti.noOutcome')}
                        {row.closure_date ? ` · ${row.closure_date}` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div ref={sentinelRef} aria-hidden className="h-px" />
              <LoadMore
                hasNextPage={Boolean(hasNextPage)}
                isFetchingNextPage={isFetchingNextPage}
                loadMore={loadMore}
                total={data.total}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The TIPE event log, row by row. The sanctions checkbox narrows to rows
 * typed as a bidding restriction -- a STAGE of a case, never a status -- and
 * the export replays exactly these filters.
 */
function TipeTable() {
  const { t, i18n } = useTranslation(['aneel', 'common']);
  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState<string | null>(null);
  const [penaltyType, setPenaltyType] = useState('');
  const [sanctionsOnly, setSanctionsOnly] = useState(false);
  const filters = { penalty_type: penaltyType || null, sanctions_only: sanctionsOnly || null, query };

  const { data: paged, isPending, error, refetch, isPlaceholderData, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['aneel', 'bidding', 'tipe', filters],
      queryFn: ({ pageParam }) => fetchAneelTipeRows({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
        return loaded < lastPage.total ? loaded : undefined;
      },
      placeholderData: keepPreviousData,
    });
  const data = paged?.pages[0];
  const items: AneelTipeRow[] = paged?.pages.flatMap((page) => page.items) ?? [];
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({ hasNextPage, isFetchingNextPage, fetchNextPage });
  const options = data?.options ?? {};

  const amount = (value: number | null) => (value === null ? '' : currency.format(value));
  const outcome = (label: string | null, date: string | null) =>
    label ? (date ? t('aneel:bidding.tipe.log.outcomeOn', { outcome: label, date }) : label) : '';

  return (
    <div className="space-y-3" data-tipe-table>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="text-xs font-medium text-foreground">{t('aneel:bidding.tipe.log.title')}</h3>
          <p className="text-xs text-muted-foreground">{t('aneel:bidding.tipe.log.help')}</p>
        </div>
        <ExportButton
          path={`/api/v1/aneel/export/bidding-tipe?${aneelTipeParams(filters).toString()}`}
        />
      </div>
      <form
        className="flex flex-wrap items-end gap-3 rounded-md border bg-background p-3"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(draft.trim() || null);
        }}
      >
        <div className="min-w-[14rem] flex-1 space-y-1">
          <label htmlFor="aneel-tipe-query" className="text-xs font-medium text-foreground">
            {t('aneel:bidding.tipe.log.filters.search')}
          </label>
          <input
            id="aneel-tipe-query"
            type="search"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('aneel:bidding.tipe.log.filters.placeholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </div>
        <OptionSelect
          id="aneel-tipe-type"
          label={t('aneel:bidding.tipe.log.filters.type')}
          any={t('aneel:bidding.tipe.log.filters.any')}
          value={penaltyType}
          onChange={setPenaltyType}
          options={options.penalty_types}
        />
        <label className="flex items-center gap-2 text-xs text-foreground">
          <input
            id="aneel-tipe-sanctions"
            type="checkbox"
            checked={sanctionsOnly}
            onChange={(event) => setSanctionsOnly(event.target.checked)}
          />
          {t('aneel:bidding.tipe.log.filters.sanctionsOnly')}
        </label>
        <button
          type="submit"
          className="rounded-md border bg-foreground px-3 py-1.5 text-sm font-medium text-background"
        >
          {t('aneel:bidding.tipe.log.filters.submit')}
        </button>
      </form>

      {error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : isPending || !data ? (
        <SkeletonLoader rows={4} />
      ) : (
        <>
          <p
            className="text-xs text-muted-foreground"
            aria-live="polite"
            data-testid="aneel-tipe-total"
            data-loaded={items.length}
            data-total={data.total}
            data-stale={String(isPlaceholderData)}
          >
            {t('common:pagination.loaded', {
              loaded: number.format(items.length),
              total: number.format(data.total),
            })}
          </p>
          {items.length === 0 ? (
            <EmptyState hint={t('aneel:bidding.tipe.log.emptyHint')} />
          ) : (
            <div
              ref={scrollRef}
              className="max-h-[60vh] overflow-auto rounded-md border"
              role="region"
              aria-label={t('aneel:bidding.tipe.log.tableLabel')}
            >
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('aneel:bidding.tipe.log.columns.edital')}</th>
                    <th className="px-3 py-2 font-medium">{t('aneel:bidding.tipe.log.columns.date')}</th>
                    <th className="px-3 py-2 font-medium">{t('aneel:bidding.tipe.log.columns.company')}</th>
                    <th className="px-3 py-2 font-medium">{t('aneel:bidding.tipe.log.columns.type')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('aneel:bidding.tipe.log.columns.original')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('aneel:bidding.tipe.log.columns.months')}</th>
                    <th className="px-3 py-2 font-medium">{t('aneel:bidding.tipe.log.columns.guarantee')}</th>
                    <th className="px-3 py-2 font-medium">{t('aneel:bidding.tipe.log.columns.manifestation')}</th>
                    <th className="px-3 py-2 font-medium">{t('aneel:bidding.tipe.log.columns.board')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row, index) => (
                    <tr
                      key={`${row.edital_number}-${index}`}
                      className="border-t align-top"
                      data-tipe-row
                      data-row-type={row.penalty_type ?? ''}
                    >
                      <td className="px-3 py-2 tabular-nums text-foreground">{row.edital_number ?? '—'}</td>
                      <td className="px-3 py-2 tabular-nums text-muted-foreground">{row.lavratura_date ?? '—'}</td>
                      <td className="px-3 py-2 text-foreground">
                        <CompanyCell name={row.entity_name} cnpj={row.entity_cnpj} />
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{row.penalty_type ?? '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">{amount(row.original_fine)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {row.original_suspension_months === null ? '' : number.format(row.original_suspension_months)}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{row.guarantee_type ?? ''}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {[outcome(row.manifestation_outcome, row.manifestation_decision_date), amount(row.fine_after_manifestation)]
                          .filter(Boolean)
                          .join(' · ')}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {[outcome(row.board_outcome, row.board_decision_date), amount(row.fine_after_board)]
                          .filter(Boolean)
                          .join(' · ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div ref={sentinelRef} aria-hidden className="h-px" />
              <LoadMore
                hasNextPage={Boolean(hasNextPage)}
                isFetchingNextPage={isFetchingNextPage}
                loadMore={loadMore}
                total={data.total}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm font-semibold tabular-nums text-foreground">{value}</p>
    </div>
  );
}
